import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useAuthStore } from '@/store/authStore';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { useDemoStore } from '@/store/demoStore';
import { 
  Video, 
  Upload, 
  Lock, 
  Globe, 
  AlertCircle,
  Info
} from 'lucide-react';
// import useApiService from '@/services/apiService';

export default function CreateStreamPage() {
  const navigate = useNavigate();
  // const { user } = useAuthStore();
  const { isDemoMode } = useDemoStore();
  // const apiService = useApiService();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamKey, setStreamKey] = useState('');
  // We'll use this later for stream viewing
  // const [playbackId, setPlaybackId] = useState('');
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  // Handle thumbnail file selection
  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Create stream
  const handleCreateStream = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError('Please enter a stream title');
      return;
    }
    
    setIsLoading(true);
    setError('');
    
    try {
      if (isDemoMode) {
        // In demo mode, simulate stream creation
        setTimeout(() => {
          toast.success('Stream created successfully!');
          setStreamKey('demo-stream-key-' + Math.floor(Math.random() * 1000));
          // setPlaybackId('demo-playback-id-' + Math.floor(Math.random() * 1000));
          setStep(2);
          setIsLoading(false);
        }, 1500);
      } else {
        // In real mode, create stream via API
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('isPrivate', String(isPrivate));
        
        if (thumbnailFile) {
          formData.append('thumbnail', thumbnailFile);
        }
        
        const response = await fetch('/api/streams/create', {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to create stream');
        }
        
        toast.success('Stream created successfully!');
        setStreamKey(data.streamKey);
        // setPlaybackId(data.playbackId);
        setStep(2);
      }
    } catch (error) {
      console.error('Error creating stream:', error);
      toast.error('Failed to create stream');
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Go to stream dashboard
  const handleGoToDashboard = () => {
    navigate('/dashboard/streams');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center mb-8">
        <Video className="h-6 w-6 mr-2 text-primary" />
        <h1 className="text-3xl font-bold text-white">Create New Stream</h1>
      </div>
      
      {step === 1 ? (
        <div className="bg-dark-800 rounded-xl p-6 shadow-md">
          <form onSubmit={handleCreateStream}>
            {error && (
              <div className="mb-6 p-4 bg-red-900/30 border border-red-700 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-red-200">{error}</p>
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Stream Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none"
                placeholder="Enter a title for your stream"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-dark-700 text-white rounded-lg p-3 border border-dark-600 focus:border-primary focus:ring-1 focus:ring-primary focus:outline-none min-h-[120px]"
                placeholder="Describe your stream (optional)"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-white font-medium mb-2">Thumbnail Image</label>
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="relative border-2 border-dashed border-dark-600 rounded-lg p-4 hover:border-primary transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center justify-center py-4">
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-400">Click or drag to upload thumbnail</p>
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG or GIF (max 2MB)</p>
                    </div>
                  </div>
                </div>
                
                {thumbnailPreview && (
                  <div className="w-1/3">
                    <div className="aspect-video rounded-lg overflow-hidden bg-dark-700">
                      <img 
                        src={thumbnailPreview} 
                        alt="Thumbnail preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mb-8">
              <label className="block text-white font-medium mb-2">Privacy Settings</label>
              <div className="flex space-x-4">
                <div 
                  className={`flex-1 p-4 rounded-lg border cursor-pointer transition-colors ${
                    !isPrivate 
                      ? 'bg-primary/10 border-primary' 
                      : 'bg-dark-700 border-dark-600 hover:border-gray-500'
                  }`}
                  onClick={() => setIsPrivate(false)}
                >
                  <div className="flex items-center mb-2">
                    <Globe className="h-5 w-5 mr-2 text-primary" />
                    <h3 className="font-medium text-white">Public</h3>
                  </div>
                  <p className="text-sm text-gray-400">Anyone can watch your stream</p>
                </div>
                
                <div 
                  className={`flex-1 p-4 rounded-lg border cursor-pointer transition-colors ${
                    isPrivate 
                      ? 'bg-primary/10 border-primary' 
                      : 'bg-dark-700 border-dark-600 hover:border-gray-500'
                  }`}
                  onClick={() => setIsPrivate(true)}
                >
                  <div className="flex items-center mb-2">
                    <Lock className="h-5 w-5 mr-2 text-primary" />
                    <h3 className="font-medium text-white">Private</h3>
                  </div>
                  <p className="text-sm text-gray-400">Only you can see this stream</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                className="mr-4"
                onClick={() => navigate('/dashboard/streams')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-primary hover:bg-primary/90"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Creating...</span>
                  </div>
                ) : (
                  <span>Create Stream</span>
                )}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-dark-800 rounded-xl p-6 shadow-md">
          <div className="mb-6 p-4 bg-green-900/30 border border-green-700 rounded-lg flex items-start">
            <Info className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-green-200 font-medium">Stream created successfully!</p>
              <p className="text-green-300 text-sm mt-1">Your stream is ready to go. Use the stream key below with your streaming software.</p>
            </div>
          </div>
          
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Stream Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Stream Title</label>
                <p className="text-white font-medium">{title}</p>
              </div>
              
              <div className="bg-dark-700 p-4 rounded-lg">
                <label className="block text-gray-400 text-sm mb-1">Privacy</label>
                <p className="text-white font-medium flex items-center">
                  {isPrivate ? (
                    <>
                      <Lock className="h-4 w-4 mr-1 text-primary" />
                      Private
                    </>
                  ) : (
                    <>
                      <Globe className="h-4 w-4 mr-1 text-primary" />
                      Public
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Stream Configuration</h2>
            
            <div className="bg-dark-700 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-400 text-sm">Stream Key</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(streamKey);
                    toast.success('Stream key copied to clipboard');
                  }}
                  className="text-xs"
                >
                  Copy
                </Button>
              </div>
              <div className="bg-dark-900 p-3 rounded border border-dark-600 font-mono text-primary">
                {streamKey}
              </div>
              <p className="text-xs text-gray-500 mt-2">Keep this key private. You'll need it to stream using OBS or other streaming software.</p>
            </div>
            
            <div className="bg-dark-700 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-gray-400 text-sm">RTMP Ingest URL</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText('rtmp://rtmp.livepeer.com/live');
                    toast.success('RTMP URL copied to clipboard');
                  }}
                  className="text-xs"
                >
                  Copy
                </Button>
              </div>
              <div className="bg-dark-900 p-3 rounded border border-dark-600 font-mono text-white">
                rtmp://rtmp.livepeer.com/live
              </div>
            </div>
          </div>
          
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setStep(1)}
            >
              Back to Edit
            </Button>
            <Button
              onClick={handleGoToDashboard}
              className="bg-primary hover:bg-primary/90"
            >
              Go to Streams Dashboard
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
